import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, LogOut, ChevronRight, Inbox, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import BrandLogo from "@/components/Brand";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch (e) { return iso; }
}

const FU_META = {
  non_previsto: { label: "Non previsto", variant: "secondary" },
  in_arrivo: { label: "In arrivo", variant: "default" },
  disponibile: { label: "Disponibile", variant: "default" },
  in_ritardo: { label: "In ritardo", variant: "destructive" },
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState("");
  const [onlyFollowup, setOnlyFollowup] = useState(false);

  const load = async (q = "", fu = false) => {
    try {
      const params = {};
      if (q) params.search = q;
      if (fu) params.followup = "1";
      const { data } = await api.get("/staff/clients", { params });
      setRows(data);
    } catch (e) {
      toast.error("Impossibile caricare i clienti.");
      setRows([]);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => load(search, onlyFollowup), 300);
    return () => clearTimeout(t);
  }, [search, onlyFollowup]);

  // counters are computed on the unfiltered set for accuracy
  const [counts, setCounts] = useState({ in_arrivo: 0, disponibile: 0, in_ritardo: 0 });
  useEffect(() => {
    api.get("/staff/clients").then(({ data }) => {
      const c = { in_arrivo: 0, disponibile: 0, in_ritardo: 0 };
      (data || []).forEach((r) => { if (c[r.followup_state] !== undefined) c[r.followup_state] += 1; });
      setCounts(c);
    }).catch(() => {});
  }, [rows]);

  const totalClients = useMemo(() => (rows ? rows.length : "—"), [rows]);

  const handleLogout = async () => {
    await logout();
    navigate("/staff/login", { replace: true });
  };

  return (
    <div className="min-h-[100dvh] bg-background">
      <header className="fs-dark text-white">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 h-16 flex items-center justify-between">
          <BrandLogo variant="light" className="h-6" />
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-xs text-white/60">{user?.email}</span>
            <button onClick={handleLogout} data-testid="logout-btn" className="flex items-center gap-1.5 text-sm text-white/80 hover:text-white transition-colors">
              <LogOut className="h-4 w-4" /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 sm:px-8 py-8">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
          <p className="fs-eyebrow">FORMAeSALUTE · Longevity Check</p>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-700 tracking-tight">Dashboard</h1>

          <div className="mt-5 flex flex-wrap gap-3">
            <div className="fs-card px-5 py-4">
              <p className="text-2xl font-heading font-700" data-testid="stat-clients">{totalClients}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Clienti</p>
            </div>
          </div>

          {/* Follow-up Longevity Check */}
          <section className="mt-4 fs-card p-5" data-testid="followup-panel">
            <div className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-muted-foreground" />
              <h2 className="fs-eyebrow text-[10px]">Follow-up Longevity Check</h2>
            </div>
            <div className="mt-3 flex flex-wrap gap-6">
              <div><p className="text-xl font-heading font-700" data-testid="fu-inarrivo">{counts.in_arrivo}</p><p className="text-xs text-muted-foreground">In arrivo</p></div>
              <div><p className="text-xl font-heading font-700" data-testid="fu-disponibili">{counts.disponibile}</p><p className="text-xs text-muted-foreground">Disponibili</p></div>
              <div><p className="text-xl font-heading font-700" data-testid="fu-inritardo">{counts.in_ritardo}</p><p className="text-xs text-muted-foreground">In ritardo</p></div>
            </div>
            <button
              onClick={() => setOnlyFollowup((v) => !v)}
              data-testid="filter-followup"
              className={`mt-4 rounded-full border px-4 py-1.5 text-xs font-medium transition-colors ${onlyFollowup ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-secondary"}`}
            >
              {onlyFollowup ? "Mostra tutti i clienti" : "Follow-up da effettuare"}
            </button>
          </section>

          <div className="mt-6 relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="search-input"
              placeholder="Cerca per nome o cognome…"
              className="h-12 rounded-xl pl-11 text-base"
            />
          </div>
        </motion.div>

        <div className="mt-6 fs-card overflow-hidden">
          <div className="hidden md:grid grid-cols-[1.4fr_1fr_0.8fr_0.8fr_auto] gap-4 px-6 py-3.5 border-b border-border/70 bg-secondary/40">
            <span className="fs-eyebrow text-[10px]">Nome e cognome</span>
            <span className="fs-eyebrow text-[10px]">Ultimo check</span>
            <span className="fs-eyebrow text-[10px]">Check</span>
            <span className="fs-eyebrow text-[10px]">Follow-up</span>
            <span className="fs-eyebrow text-[10px] text-right">Azione</span>
          </div>

          {rows === null && (
            <div className="p-10 grid place-items-center">
              <div className="h-7 w-7 rounded-full border-2 border-foreground/20 border-t-foreground animate-spin" />
            </div>
          )}

          {rows && rows.length === 0 && (
            <div className="p-12 text-center" data-testid="empty-state">
              <Inbox className="h-8 w-8 mx-auto text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">
                {search ? "Nessun risultato per la ricerca." : (onlyFollowup ? "Nessun follow-up da effettuare." : "Nessun cliente registrato.")}
              </p>
            </div>
          )}

          {rows && rows.map((r) => {
            const fu = FU_META[r.followup_state] || FU_META.non_previsto;
            return (
              <button
                key={r.client_id}
                onClick={() => navigate(`/staff/cliente/${r.latest_check_id}`)}
                data-testid={`row-${r.client_id}`}
                className="w-full text-left grid grid-cols-[1fr_auto] md:grid-cols-[1.4fr_1fr_0.8fr_0.8fr_auto] gap-3 md:gap-4 items-center px-5 md:px-6 py-4 border-b border-border/60 last:border-0 hover:bg-secondary/50 transition-colors group"
              >
                <div className="min-w-0">
                  <p className="font-semibold truncate">{`${r.first_name} ${r.last_name}`.trim() || "—"}</p>
                  <p className="md:hidden text-xs text-muted-foreground mt-0.5">{formatDate(r.latest_date)}</p>
                  {r.followup_state === "in_ritardo" && (
                    <span className="md:hidden inline-flex mt-1">
                      <Badge variant="destructive" className="rounded-full text-[10px]" data-testid={`recontact-${r.client_id}`}>
                        Da ricontattare · {Math.abs(r.followup_days || 0)}g fa
                      </Badge>
                    </span>
                  )}
                </div>
                <span className="hidden md:block text-sm text-muted-foreground">{formatDate(r.latest_date)}</span>
                <span className="hidden md:block text-sm text-muted-foreground">{r.check_count}</span>
                <span className="hidden md:block">
                  {r.followup_state === "in_ritardo" ? (
                    <Badge variant="destructive" className="rounded-full font-medium" data-testid={`recontact-${r.client_id}`}>
                      Da ricontattare · {Math.abs(r.followup_days || 0)}g fa
                    </Badge>
                  ) : (
                    <Badge variant={fu.variant} className="rounded-full font-medium" data-testid={`fu-state-${r.client_id}`}>{fu.label}</Badge>
                  )}
                </span>
                <span className="flex items-center justify-end gap-1 text-sm font-medium text-foreground/70 group-hover:text-foreground transition-colors">
                  <span className="hidden sm:inline">Apri</span>
                  <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </button>
            );
          })}
        </div>
      </main>
    </div>
  );
}
